import type { AnswerDelta, Delta } from './apply-deltas'
import type { CustomizationSummary, ProjectTemplate, RefinementQuestion } from '../types'

/** Resolve the display label for a single answer, matching `buildCustomizationSummary` (no delta scoring). */
function answerLabelForKey(
  value: string,
  q: RefinementQuestion | undefined
): string {
  if (!q) return value
  const fromString = q.stringOptions?.find((o) => o.value === value)
  if (fromString?.label) return fromString.label
  const fromNumber = q.numberOptions?.find((o) => o.value === Number(value))
  return fromNumber?.label ?? value
}

/**
 * All non-empty Q&A labels in `refinementQuestions` order, for chip / badge display.
 */
export function buildAnswerChipLabels(
  template: ProjectTemplate,
  answers: Record<string, string>
): Array<string> {
  const out: Array<string> = []
  const seen = new Set<string>()
  for (const q of template.refinementQuestions ?? []) {
    if (seen.has(q.key)) continue
    seen.add(q.key)
    const raw = answers[q.key]
    if (typeof raw !== 'string' || raw.trim() === '') continue
    out.push(answerLabelForKey(raw, q))
  }
  return out
}

/**
 * True when this delta is expected to change the plan in a way users notice
 * (empty `{}` is not substantive).
 */
export function isSubstantiveDelta(d: Delta | undefined): boolean {
  if (!d) return false
  if (d.adjustHours !== undefined && d.adjustHours !== 0) return true
  if (d.adjustCost) return true
  if (d.addSteps && d.addSteps.length > 0) return true
  if (d.removeStepIndices && d.removeStepIndices.length > 0) return true
  if (d.modifySteps && d.modifySteps.length > 0) return true
  if (d.addTools && d.addTools.length > 0) return true
  if (d.addMaterials && d.addMaterials.length > 0) return true
  return false
}

function scoreQuestion(q: RefinementQuestion | undefined): number {
  if (!q) return 0
  let s = 0
  if (q.affectsTime) s += 50
  if (q.affectsTools) s += 30
  if (q.affectsMaterials) s += 30
  if (q.affectsSteps) s += 20
  if (q.affectsDifficulty) s += 5
  return s
}

/**
 * Pick up to `maxValues` "key" answer labels for the header line, prioritizing
 * answers that map to substantive deltas, then high-impact questions.
 */
export function buildCustomizationSummary(
  template: ProjectTemplate,
  answers: Record<string, string>,
  deltas: Array<AnswerDelta>,
  maxValues = 3
): CustomizationSummary | undefined {
  const entries = Object.entries(answers).filter(
    ([, v]) => typeof v === 'string' && v.trim() !== ''
  )
  if (entries.length === 0) return undefined

  const byKey = new Map(
    template.refinementQuestions?.map((q, i) => [q.key, { q, order: i } as const]) ?? []
  )

  const scored = entries.map(([key, value]) => {
    const meta = byKey.get(key)
    const match = deltas.find((d) => d.questionKey === key && d.answerValue === value)
    const deltaBoost = isSubstantiveDelta(match?.delta) ? 100 : 0
    const score = deltaBoost + scoreQuestion(meta?.q)
    return { key, value, order: meta?.order ?? 999, score, q: meta?.q }
  })

  scored.sort((a, b) => b.score - a.score || a.order - b.order)

  const values = scored
    .slice(0, maxValues)
    .map(({ value, q }) => answerLabelForKey(value, q))

  return { label: 'Personalized for your', values }
}
